// src/pages/Trend/components/analysis/consumer-reaction/KeywordsChart.js
import React, { memo, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { ErrorMessage } from '../../../../../components/common/UI';
import { BOLSTER_CHART_STYLE } from '../common/BolsterColors';
import * as d3 from 'd3';
import './KeywordsChart.css';

// Style Configuration
const BBC_STYLE = {
  FONT_FAMILY: BOLSTER_CHART_STYLE.FONT_FAMILY,
  PRIMARY_COLOR: '#1380A1',
  SECONDARY_COLOR: '#FAAB18',
  HIGHLIGHT_COLOR: '#D55E00',
  TEXT_COLOR: '#333333',
  LIGHT_TEXT: '#666666'
};

// Render filter label with monospace number for alignment
const renderFilterLabel = (label) => {
  const match = label.match(/^(\d+)(.*)$/);
  if (match) {
    return (
      <>
        <span className="filter-label-number">{match[1]}</span>
        {match[2]}
      </>
    );
  }
  return label;
};

// Refined color palette with gradients
const CATEGORY_CONFIG = {
  effects: {
    color: '#0891b2',
    gradient: ['#06b6d4', '#0891b2'],
    lightBg: 'rgba(6, 182, 212, 0.08)'
  },
  texture: {
    color: '#6366f1',
    gradient: ['#818cf8', '#6366f1'],
    lightBg: 'rgba(99, 102, 241, 0.08)'
  },
  satisfaction: {
    color: '#10b981',
    gradient: ['#34d399', '#10b981'],
    lightBg: 'rgba(16, 185, 129, 0.08)'
  },
  skin_condition: {
    color: '#ec4899',
    gradient: ['#f472b6', '#ec4899'],
    lightBg: 'rgba(236, 72, 153, 0.08)'
  }
};

// Process and flatten keyword data
const processKeywordData = (data) => {
  if (!data?.keywords) return { all: [], byCategory: {} };

  const byCategory = {};
  const all = [];

  Object.entries(data.keywords).forEach(([category, categoryData]) => {
    if (categoryData?.labels && categoryData?.values) {
      byCategory[category] = categoryData.labels.map((label, idx) => ({
        text: label,
        value: categoryData.values[idx],
        category
      }));
      all.push(...byCategory[category]);
    }
  });

  return { all, byCategory };
};

// Get top keywords across all categories
const getTopKeywords = (data, count = 3) => {
  return [...data].sort((a, b) => b.value - a.value).slice(0, count);
};

// Get dominant category
const getDominantCategory = (byCategory) => {
  let maxTotal = 0;
  let dominant = null;

  Object.entries(byCategory).forEach(([category, keywords]) => {
    const total = keywords.reduce((sum, k) => sum + k.value, 0);
    if (total > maxTotal) {
      maxTotal = total;
      dominant = { category, total };
    }
  });

  return dominant;
};

// Bubble Chart Component using D3 force simulation
const BubbleChart = ({ data, activeCategories, translate, width = 500, height = 400 }) => {
  const svgRef = useRef(null);
  const [hoveredBubble, setHoveredBubble] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({ x: 0, y: 0 });

  const filteredData = useMemo(() => {
    return data.filter(d => activeCategories.includes(d.category));
  }, [data, activeCategories]);

  useEffect(() => {
    if (!svgRef.current || filteredData.length === 0) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    // Create gradient definitions
    const defs = svg.append('defs');

    Object.entries(CATEGORY_CONFIG).forEach(([category, config]) => {
      const gradient = defs.append('radialGradient')
        .attr('id', `gradient-${category}`)
        .attr('cx', '30%')
        .attr('cy', '30%')
        .attr('r', '70%');

      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', config.gradient[0])
        .attr('stop-opacity', 0.9);

      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', config.gradient[1])
        .attr('stop-opacity', 1);
    });

    // Calculate bubble sizes
    const maxValue = d3.max(filteredData, d => d.value);
    const minValue = d3.min(filteredData, d => d.value);
    const radiusScale = d3.scaleSqrt()
      .domain([minValue, maxValue])
      .range([18, 55]);

    // Create nodes with radius
    const nodes = filteredData.map(d => ({
      ...d,
      radius: radiusScale(d.value)
    }));

    // Force simulation
    const simulation = d3.forceSimulation(nodes)
      .force('charge', d3.forceManyBody().strength(5))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(d => d.radius + 3).strength(0.9))
      .force('x', d3.forceX(width / 2).strength(0.05))
      .force('y', d3.forceY(height / 2).strength(0.05));

    // Create container group
    const g = svg.append('g');

    // Create bubble groups
    const bubbles = g.selectAll('.bubble-group')
      .data(nodes)
      .enter()
      .append('g')
      .attr('class', 'bubble-group')
      .style('cursor', 'pointer');

    // Add circles
    bubbles.append('circle')
      .attr('r', d => d.radius)
      .attr('fill', d => `url(#gradient-${d.category})`)
      .attr('stroke', d => CATEGORY_CONFIG[d.category].color)
      .attr('stroke-width', 1.5)
      .attr('opacity', 0.9)
      .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))')
      .style('transition', 'all 0.2s ease');

    // Add text labels
    bubbles.append('text')
      .attr('text-anchor', 'middle')
      .attr('dy', '0.35em')
      .attr('fill', 'white')
      .attr('font-size', d => Math.min(d.radius / 2.5, 14))
      .attr('font-weight', '600')
      .attr('pointer-events', 'none')
      .text(d => d.text.length > 6 ? d.text.slice(0, 5) + '…' : d.text)
      .style('text-shadow', '0 1px 2px rgba(0,0,0,0.3)');

    // Hover interactions
    bubbles
      .on('mouseenter', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('r', d.radius * 1.15)
          .attr('opacity', 1)
          .style('filter', 'drop-shadow(0 4px 12px rgba(0,0,0,0.25))');

        d3.select(this).select('text')
          .transition()
          .duration(200)
          .attr('font-size', Math.min(d.radius / 2.2, 16));

        const rect = svgRef.current.getBoundingClientRect();
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10
        });
        setHoveredBubble(d);
      })
      .on('mousemove', function(event) {
        const rect = svgRef.current.getBoundingClientRect();
        setTooltipPos({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top - 10
        });
      })
      .on('mouseleave', function(event, d) {
        d3.select(this).select('circle')
          .transition()
          .duration(200)
          .attr('r', d.radius)
          .attr('opacity', 0.9)
          .style('filter', 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))');

        d3.select(this).select('text')
          .transition()
          .duration(200)
          .attr('font-size', Math.min(d.radius / 2.5, 14));

        setHoveredBubble(null);
      });

    // Update positions on simulation tick
    simulation.on('tick', () => {
      bubbles.attr('transform', d => {
        // Keep bubbles within bounds
        d.x = Math.max(d.radius, Math.min(width - d.radius, d.x));
        d.y = Math.max(d.radius, Math.min(height - d.radius, d.y));
        return `translate(${d.x},${d.y})`;
      });
    });

    // Initial animation
    bubbles.attr('opacity', 0)
      .transition()
      .duration(600)
      .delay((d, i) => i * 30)
      .attr('opacity', 1);

    return () => {
      simulation.stop();
    };
  }, [filteredData, width, height]);

  return (
    <div className="bubble-chart-container">
      <svg ref={svgRef} width={width} height={height} />
      {hoveredBubble && (
        <div
          className="bubble-tooltip"
          style={{
            left: tooltipPos.x,
            top: tooltipPos.y,
            transform: 'translate(-50%, -100%)'
          }}
        >
          <div className="tooltip-keyword">{hoveredBubble.text}</div>
          <div className="tooltip-category" style={{ color: CATEGORY_CONFIG[hoveredBubble.category].color }}>
            {translate(`trendPage.consumerReaction.keywords.${hoveredBubble.category === 'skin_condition' ? 'skinCondition' : hoveredBubble.category}`)}
          </div>
          <div className="tooltip-value">
            {translate('trendPage.consumerReaction.keywords.frequency')}: <strong>{hoveredBubble.value.toLocaleString()}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

// Narrative Component
const KeywordNarrative = ({ primaryData, secondaryData, hasSecondaryData, categoryNames, translate }) => {
  const topPrimary = getTopKeywords(primaryData.all, 1)[0];
  const dominantPrimary = getDominantCategory(primaryData.byCategory);
  const totalKeywords = primaryData.all.length;

  if (!topPrimary) return null;

  if (!hasSecondaryData) {
    return (
      <div className="keyword-narrative">
        <h2 className="narrative-title">
          <span className="highlight-keyword" style={{
            color: CATEGORY_CONFIG[topPrimary.category]?.color,
            borderBottom: `2px solid ${CATEGORY_CONFIG[topPrimary.category]?.color}`
          }}>
            "{topPrimary.text}"
          </span>
          {' '}{translate('trendPage.consumerReaction.keywords.narrative.topKeyword')}
        </h2>
        <div className="narrative-subtitle">
          <span className="stat-pill" style={{ background: CATEGORY_CONFIG[dominantPrimary?.category]?.lightBg }}>
            {categoryNames[dominantPrimary?.category]} {translate('trendPage.consumerReaction.keywords.narrative.dominantCategory')}
          </span>
          <span className="stat-divider">•</span>
          <span>{totalKeywords}{translate('trendPage.consumerReaction.keywords.narrative.keywordsAnalyzed')}</span>
        </div>
      </div>
    );
  }

  // Comparison mode
  const topSecondary = getTopKeywords(secondaryData.all, 1)[0];
  const dominantSecondary = getDominantCategory(secondaryData.byCategory);

  return (
    <div className="keyword-narrative">
      <h2 className="narrative-title">
        {translate('trendPage.consumerReaction.keywords.narrative.comparisonTitle')}
      </h2>
      <div className="narrative-comparison">
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.PRIMARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set1'))}:</span>
          <span className="highlight-keyword" style={{
            color: CATEGORY_CONFIG[topPrimary?.category]?.color
          }}>
            "{topPrimary?.text}"
          </span>
          <span className="comparison-category">({categoryNames[dominantPrimary?.category]})</span>
        </div>
        <div className="comparison-item">
          <div className="comparison-dot" style={{ background: BBC_STYLE.SECONDARY_COLOR }} />
          <span className="comparison-label">{renderFilterLabel(translate('trendPage.filters.set2'))}:</span>
          <span className="highlight-keyword" style={{
            color: CATEGORY_CONFIG[topSecondary?.category]?.color
          }}>
            "{topSecondary?.text}"
          </span>
          <span className="comparison-category">({categoryNames[dominantSecondary?.category]})</span>
        </div>
      </div>
    </div>
  );
};

// Category Toggle Pills
const CategoryPills = ({ categories, activeCategories, onToggle }) => {
  return (
    <div className="category-pills">
      {Object.entries(categories).map(([key, name]) => {
        const isActive = activeCategories.includes(key);
        const config = CATEGORY_CONFIG[key];

        return (
          <button
            key={key}
            className={`category-pill ${isActive ? 'active' : ''}`}
            style={{
              '--pill-color': config.color,
              '--pill-bg': config.lightBg
            }}
            onClick={() => onToggle(key)}
          >
            <span className="pill-dot" />
            {name}
          </button>
        );
      })}
    </div>
  );
};

// Main Component
const KeywordsChart = ({ data }) => {
  const { translate } = useLanguage();

  const categoryNames = useMemo(() => ({
    effects: translate('trendPage.consumerReaction.keywords.effects'),
    texture: translate('trendPage.consumerReaction.keywords.texture'),
    satisfaction: translate('trendPage.consumerReaction.keywords.satisfaction'),
    skin_condition: translate('trendPage.consumerReaction.keywords.skinCondition')
  }), [translate]);

  const [activeCategories, setActiveCategories] = useState(Object.keys(CATEGORY_CONFIG));
  const [activeTab, setActiveTab] = useState('primary');

  const toggleCategory = useCallback((category) => {
    setActiveCategories(prev => {
      if (prev.includes(category)) {
        // Don't allow deselecting all
        if (prev.length === 1) return prev;
        return prev.filter(cat => cat !== category);
      }
      return [...prev, category];
    });
  }, []);

  if (!data?.primary?.keywords) {
    return <ErrorMessage message={translate('trendPage.consumerReaction.errors.noKeywordData')} />;
  }

  const { primary, secondary } = data;
  const hasSecondaryData = secondary?.keywords !== undefined;

  const primaryProcessed = processKeywordData(primary);
  const secondaryProcessed = hasSecondaryData ? processKeywordData(secondary) : null;

  return (
    <div className="keywords-chart-container" style={{ fontFamily: BBC_STYLE.FONT_FAMILY }}>
      {/* Narrative Section */}
      <KeywordNarrative
        primaryData={primaryProcessed}
        secondaryData={secondaryProcessed}
        hasSecondaryData={hasSecondaryData}
        categoryNames={categoryNames}
        translate={translate}
      />

      {/* Filter Toggle - only show when comparison data exists */}
      {hasSecondaryData && (
        <div className="keywords-tab-header">
          <button
            className={`keywords-tab-btn ${activeTab === 'primary' ? 'active' : ''}`}
            onClick={() => setActiveTab('primary')}
          >
            <span className="tab-dot primary" />
            {renderFilterLabel(translate('trendPage.filters.set1'))}
          </button>
          <button
            className={`keywords-tab-btn ${activeTab === 'secondary' ? 'active' : ''}`}
            onClick={() => setActiveTab('secondary')}
          >
            <span className="tab-dot secondary" />
            {renderFilterLabel(translate('trendPage.filters.set2'))}
          </button>
        </div>
      )}

      {/* Category Filter Pills */}
      <CategoryPills
        categories={categoryNames}
        activeCategories={activeCategories}
        onToggle={toggleCategory}
      />

      {/* Bubble Chart - Single chart with tab switching */}
      <div className="bubble-charts-wrapper">
        <div className="bubble-chart-section single">
          <BubbleChart
            data={hasSecondaryData
              ? (activeTab === 'primary' ? primaryProcessed.all : secondaryProcessed.all)
              : primaryProcessed.all
            }
            activeCategories={activeCategories}
            translate={translate}
            width={850}
            height={280}
          />
        </div>
      </div>
    </div>
  );
};

export default memo(KeywordsChart);
